import { QueryClientProvider } from '@tanstack/react-query'
import 'bootstrap/dist/css/bootstrap.min.css'
import { useEffect, useState } from 'react'
import ReactDOM from 'react-dom/client'
import { AuthProvider } from './context/authentication/auth-context'
import { queryClient } from './services/queryClient'

import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import 'chart.js/auto'
import { PreferencesProvider } from 'context/preferences/PreferencesContext'
import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

import { Z_INDEX } from 'styles/z-index'
import LoadingScreen from './components/Layout/LoadingScreen'
import { useAuth } from './context/authentication/useAuth'
import './main.css'
// First Bite design-system CSS variables (--fbui-*) consumed by @firstbite/ui
// components. Imported after main.css so the tokens are defined app-wide.
import '@firstbite/ui/tokens.css'
import useApiService from './services/api'
import { useGetPreferences } from 'services/preferences'
import IntercomMessenger from './components/IntercomMessenger'
import Desktop from 'desktop'
import Mobile from 'mobile'

import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { publicFeatureFlags } from 'utils/publicFeatureFlags'
import { DownloadProvider, DownloadWidget } from 'features/download-widget'
import { useUserAgent } from 'hooks/useUserAgent'

function Root() {
  const api = useApiService()
  const { isAuthenticated, isCheckingAuth, isLimited } = useAuth()
  const { isMobile } = useUserAgent()
  const authenticatedFlags = useFeatureFlagService()

  const [enableMobileAppPublic, setEnableMobileAppPublic] = useState(false)
  const [publicFlagsLoaded, setPublicFlagsLoaded] = useState(false)

  // Load public feature flags when not authenticated
  useEffect(() => {
    if (!isAuthenticated && !isCheckingAuth) {
      publicFeatureFlags.loadFlags().then(() => {
        setEnableMobileAppPublic(
          publicFeatureFlags.isEnabled('enable_mobile_app')
        )
        setPublicFlagsLoaded(true)
      })
    }
  }, [isAuthenticated, isCheckingAuth])

  const enableMobileApp = isAuthenticated
    ? authenticatedFlags.enableMobileApp
    : enableMobileAppPublic
  const isLoadingFeatureFlags = isAuthenticated
    ? authenticatedFlags.isLoading
    : !publicFlagsLoaded

  // Common data shared across the app
  // We load it here so its available faster
  api.useGetActiveCompanyCategoriesAndTypes(!isAuthenticated || isLimited)
  api.useGetUsers(!isAuthenticated || isLimited)
  api.useGetSalesStages(!isAuthenticated || isLimited)
  api.useGetTags('products', !isAuthenticated || isLimited)
  api.useGetTags('contacts', !isAuthenticated || isLimited)
  api.useGetProducts(!isAuthenticated)
  api.useGetCampaigns(undefined, !isAuthenticated || isLimited)
  useGetPreferences({
    query: {
      enabled: !(!isAuthenticated || isLimited),
      refetchOnMount: false,
    },
  })

  useEffect(() => {
    // If this is a production build, load the ga4 script
    if (process.env.NODE_ENV === 'production') {
      const src_script = document.createElement('script')
      src_script.src =
        'https://www.googletagmanager.com/gtag/js?id=G-2DXQWDV89Z'
      src_script.async = true

      const script = document.createElement('script')
      script.innerHTML =
        " window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date()); gtag('config', 'G-2DXQWDV89Z'); "

      document.head.appendChild(src_script)
      document.head.appendChild(script)

      return () => {
        document.head.removeChild(src_script)
        document.head.removeChild(script)
      }
    }
  }, [])

  if (isCheckingAuth || isLoadingFeatureFlags) {
    return <LoadingScreen />
  }

  return (
    <>
      {isMobile && (enableMobileApp || !isAuthenticated) ? (
        <Mobile />
      ) : (
        <Desktop />
      )}
      <IntercomMessenger />
    </>
  )
}

const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY
const posthogHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST

// PostHog is enabled in non-dev environments, or when VITE_ENABLE_POSTHOG=true in .env.local
const isPostHogEnabled =
  import.meta.env.MODE !== 'development' ||
  import.meta.env.VITE_ENABLE_POSTHOG === 'true'

// Initialize PostHog only when enabled and API key is configured
if (isPostHogEnabled && posthogKey && typeof window !== 'undefined') {
  posthog.init(posthogKey, {
    api_host: posthogHost,
    // Disable automatic pageview capture - we handle this manually via usePostHogPageview hook
    // to properly track SPA route changes
    capture_pageview: false,
    capture_pageleave: true,
    // Disable auto-start of session recording - we start it manually after
    // authentication to exclude internal users (is_staff)
    disable_session_recording: true,
  })
}

const AppProviders = ({ children }: { children: React.ReactNode }) => {
  if (!isPostHogEnabled) {
    return <>{children}</>
  }
  return <PostHogProvider client={posthog}>{children}</PostHogProvider>
}

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <AppProviders>
    <QueryClientProvider client={queryClient}>
      <PreferencesProvider>
        <AuthProvider>
          <DownloadProvider>
            <Root />
            <DownloadWidget />
            <ReactQueryDevtools />
            <ToastContainer style={{ zIndex: Z_INDEX.NOTIFICATION }} />
          </DownloadProvider>
        </AuthProvider>
      </PreferencesProvider>
    </QueryClientProvider>
  </AppProviders>
)
